import * as React from 'react'
import { useIntl } from 'react-intl'
import LineTagAltIcon from 'LuxKit/Icons/line/LineTagAltIcon'
import LineGlassMartiniAltIcon from 'LuxKit/Icons/line/LineGlassMartiniAltIcon'
import LineGlobeAltIcon from 'LuxKit/Icons/line/LineGlobeAltIcon'
import LineClockDollarIcon from 'LuxKit/Icons/line/LineClockDollarIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import LineParcelIcon from 'LuxKit/Icons/line/LineParcelIcon'
import LineUserPlusIcon from 'LuxKit/Icons/line/LineUserPlusIcon'
import LineUsdCircleIcon from 'LuxKit/Icons/line/LineUsdCircleIcon'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'

const tagIcon = <LineTagAltIcon size="L" />
const martiniIcon = <LineGlassMartiniAltIcon size="L" />
const globeIcon = <LineGlobeAltIcon size="L" />
const clockDollarIcon = <LineClockDollarIcon size="L" />
const phoneIcon = <LinePhoneIcon size="L" />
const parcelIcon = <LineParcelIcon size="L" />
const userPlusIcon = <LineUserPlusIcon size="L" />
const usdCircleIcon = <LineUsdCircleIcon size="L" />

export interface WhyBookWithUsItem {
  key: string
  Icon: React.ReactElement
  heading: string
  body: string
  link?: {
    text: string
    to?: string
    href?: string
  }
}

export function useWhyBookWithUsContent(): Array<WhyBookWithUsItem> {
  const intl = useIntl()
  const showDreamBlogLinks = useFeatureFlag(OptimizelyFeatureFlags.showDreamBlogLinks)

  return [
    {
      key: 'save-up-to-70',
      Icon: tagIcon,
      heading: intl.formatMessage({
        id: 'jZ5pnlDXBqaKjOVoqZHn',
        defaultMessage: 'Save up to 70% off',
        description: 'Why book with us - save up to 70% heading on the about us page',
      }),
      body: intl.formatMessage({
        id: 'fbpY7yCjmZHZFlPkcP-m',
        defaultMessage: 'We negotiate directly with our travel partners to secure exclusive offers you won’t find elsewhere.',
        description: 'Why book with us - save up to 70% body text on the offer page',
      }),
      link: {
        text: intl.formatMessage({
          id: '26QZSbQz5JQlGerUHoeI',
          defaultMessage: 'Browse offers',
          description: 'Why book with us - link to browse travel offers on the about us page',
        }),
        to: '/',
      },
    },
    {
      key: 'bonus-vip-inclusions',
      Icon: martiniIcon,
      heading: intl.formatMessage({
        id: 'MOCFs_tssQhja3v6bafV',
        defaultMessage: 'Bonus VIP inclusions',
        description: 'Why book with us - bonus VIP inclusions heading on the about us page',
      }),
      body: intl.formatMessage({
        id: '_-FvKdj2NTx_8iUQ5I7k',
        defaultMessage: 'Handpicked inclusions and exclusive extras will make your trip even more memorable.',
        description: 'Why book with us - bonus VIP inclusions body text on the about us page',
      }),
    },
    {
      key: 'one-stop-destination',
      Icon: globeIcon,
      heading: intl.formatMessage({
        id: 'CTJY9Ehd6AaREX7r1TxG',
        defaultMessage: 'One-stop destination',
        description: 'Why book with us - one-stop destination heading on the about us page',
      }),
      body: intl.formatMessage({
        id: '5YvibZa0MXCb2ePsss0R',
        defaultMessage: 'Discover 1,000s of flights, hotels, packages, tours, cruise, experiences, car hire & more.',
        description: 'Why book with us - one-stop destination body text on the about us page',
      }),
    },
    {
      key: 'flexible-bookings',
      Icon: clockDollarIcon,
      heading: intl.formatMessage({
        id: 'VOw25lNRiuq1RrPW1gfn',
        defaultMessage: 'Flexible bookings',
        description: 'Why book with us - flexible bookings heading in the booking flow',
      }),
      body: intl.formatMessage({
        id: 'U_w9SoNQzVYkCPIXSROP',
        defaultMessage: 'Travel with peace of mind with our flexible cancellation policies.',
        description: 'Why book with us - flexible bookings body text on the about us page',
      }),
    },
    {
      key: 'customer-service',
      Icon: phoneIcon,
      heading: intl.formatMessage({
        id: 'YpIh65PSMHQ49V9QwQ1Q',
        defaultMessage: '24/7 customer service',
        description: 'Why book with us - customer service heading on the about us page',
      }),
      body: intl.formatMessage({
        id: 'qKGCTYkvZmmwF8tC27IU',
        defaultMessage: 'Regardless of your time zone, we offer round-the-clock service.',
        description: 'Why book with us - customer service body text on the about us page',
      }),
    },
    ...(showDreamBlogLinks ? [{
      key: 'local-travel-guides',
      Icon: parcelIcon,
      heading: intl.formatMessage({
        id: 'xNF8_j5ZzAs9C9gaQRVE',
        defaultMessage: 'Local travel guides and inspiration',
        description: 'Why book with us - local travel guides heading on the about us page',
      }),
      body: intl.formatMessage({
        id: 'sbH4vuavUGe86N10RwnZ',
        defaultMessage: 'Be inspired by expert recommendations and local travel tips.',
        description: 'Why book with us - local travel guides body text on the about us page',
      }),
      link: {
        text: intl.formatMessage({
          id: '_XwbMqNgMGF_E1XF4_DA',
          defaultMessage: 'Get inspired',
          description: 'Why book with us - link to travel inspiration content',
        }),
        href: 'https://luxuryescapes.com/inspiration',
      },
    }] : []),
    {
      key: 'refer-a-friend',
      Icon: userPlusIcon,
      heading: intl.formatMessage({
        id: '5QNzH2rsGyERw3-K8XGF',
        defaultMessage: 'Refer a friend',
        description: 'Why book with us - refer a friend heading on the about us page',
      }),
      body: intl.formatMessage({
        id: 'Di1m978ABuktqeWdlc1p',
        defaultMessage: 'Refer a friend and you’ll both receive credit towards your next escape.',
        description: 'Why book with us - refer a friend body text on the about us page',
      }),
      link: {
        text: intl.formatMessage({
          id: 'ncLiY7i6xchlQXKsXiFX',
          defaultMessage: 'Learn more',
          description: 'Why book with us - link to refer a friend page on the about us page',
        }),
        to: '/refer',
      },
    },
    {
      key: 'best-price-guarantee',
      Icon: usdCircleIcon,
      heading: intl.formatMessage({
        id: 'naeZjLHnney9h_T9ElmZ',
        defaultMessage: 'Best price guarantee',
        description: 'Why book with us - best price guarantee heading on the about us page',
      }),
      body: intl.formatMessage({
        id: '2vEufwVDci9fMzp4W5aq',
        defaultMessage: 'In the unlikely event that you find a better price for the same offer, we promise to match it.',
        description: 'Why book with us - best price guarantee body text on the about us page',
      }),
      link: {
        text: intl.formatMessage({
          id: 'XBwsMwyAax2L4u2b2WRK',
          defaultMessage: 'Learn more',
          description: 'Why book with us - link to best price guarantee page',
        }),
        to: '/best-price-guarantee',
      },
    },
  ]
}
